import React, { useState, useEffect, useCallback } from 'react';
import { TransaksiPembayaran } from '../../types/index.js';
import {
  Building2,
  Clock,
  Copy,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  X,
  Smartphone,
  Monitor,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import { api } from '../../services/api.js';

// ─── Duitku VA Bank Codes ───────────────────────────────────────────────────
export const VA_BANK_LIST = [
  {
    code: 'BC',  // Duitku code for BCA VA
    bank: 'BCA',
    label: 'Bank Central Asia',
    color: '#003d82',
    bgColor: 'bg-blue-900',
    textColor: 'text-blue-900',
    borderColor: 'border-blue-900',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM BCA & PIN',
      'Pilih Transaksi Lainnya → Transfer → ke Rekening BCA Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Pastikan nominal & nama sesuai, lalu konfirmasi',
      'Simpan struk sebagai bukti pembayaran',
    ],
    mBankingSteps: [
      'Buka aplikasi myBCA',
      'Pilih menu "Transfer" → "Virtual Account"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi nominal & nama tujuan',
      'Masukkan PIN m-BCA untuk konfirmasi',
    ],
  },
  {
    code: 'M2',  // Mandiri VA
    bank: 'MANDIRI',
    label: 'Bank Mandiri',
    color: '#003087',
    bgColor: 'bg-yellow-600',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-600',
    logo: '🏛',
    atmSteps: [
      'Masukkan kartu ATM Mandiri & PIN',
      'Pilih Bayar/Beli → Lainnya → Transfer → ke Rekening Mandiri Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Verifikasi nominal & nama, lalu konfirmasi',
    ],
    mBankingSteps: [
      'Buka Livin\' by Mandiri',
      'Pilih "Bayar" → "Virtual Account"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi transaksi dengan PIN/biometrik',
    ],
  },
  {
    code: 'I1',  // BNI VA
    bank: 'BNI',
    label: 'Bank Negara Indonesia',
    color: '#e8660a',
    bgColor: 'bg-orange-600',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-600',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM BNI & PIN',
      'Pilih Menu Lain → Transfer → ke Rekening BNI → ke Rekening BNI Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi nominal & nama, lalu konfirmasi',
    ],
    mBankingSteps: [
      'Buka BNI Mobile Banking',
      'Pilih "Transfer" → "Virtual Account Billing"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi nominal & nama tujuan, lalu konfirmasi dengan PIN',
    ],
  },
  {
    code: 'BR',  // BRI VA
    bank: 'BRI',
    label: 'Bank Rakyat Indonesia',
    color: '#00529b',
    bgColor: 'bg-sky-800',
    textColor: 'text-sky-800',
    borderColor: 'border-sky-800',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM BRI & PIN',
      'Pilih Transaksi Lain → Pembayaran → Lainnya → BRIVA',
      'Masukkan Nomor VA yang tertera',
      'Verifikasi nominal & nama, konfirmasi Ya',
    ],
    mBankingSteps: [
      'Buka BRImo',
      'Pilih "Pembayaran" → "BRIVA"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi nominal, lalu masukkan PIN',
    ],
  },
  {
    code: 'B1',  // CIMB Niaga
    bank: 'CIMB NIAGA',
    label: 'CIMB Niaga',
    color: '#a32020',
    bgColor: 'bg-red-800',
    textColor: 'text-red-800',
    borderColor: 'border-red-800',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM CIMB Niaga & PIN',
      'Pilih Pembayaran → Lanjut → Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Verifikasi nominal & nama, lalu konfirmasi',
    ],
    mBankingSteps: [
      'Buka OCTO Mobile',
      'Pilih "Transfer" → "Transfer ke Rekening CIMB Niaga / Rekening Ponsel"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi nominal, lalu masukkan PIN',
    ],
  },
  {
    code: 'BT',  // Permata
    bank: 'PERMATA',
    label: 'Bank Permata',
    color: '#00835f',
    bgColor: 'bg-emerald-700',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-700',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM Permata & PIN',
      'Pilih Transaksi Lainnya → Pembayaran → Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Verifikasi nominal & nama, lalu konfirmasi',
    ],
    mBankingSteps: [
      'Buka PermataMobile X',
      'Pilih "Bayar Tagihan" → "Virtual Account"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi transaksi',
    ],
  },
  {
    code: 'VA',  // Maybank
    bank: 'MAYBANK',
    label: 'Maybank',
    color: '#ffc107',
    bgColor: 'bg-amber-500',
    textColor: 'text-amber-500',
    borderColor: 'border-amber-500',
    logo: '🏦',
    atmSteps: [
      'Masukkan kartu ATM Maybank & PIN',
      'Pilih Pembayaran / Top Up → Virtual Account',
      'Masukkan Nomor VA yang tertera',
      'Verifikasi nominal & nama, lalu konfirmasi',
    ],
    mBankingSteps: [
      'Buka M2U ID',
      'Pilih "Bayar" → "Virtual Account"',
      'Masukkan Nomor VA yang tertera',
      'Konfirmasi pembayaran',
    ],
  },
];

// ─── Props ──────────────────────────────────────────────────────────────────
interface VAPaymentModalProps {
  transaction: TransaksiPembayaran & {
    vaNumber?: string;
    referenceNo?: string;
    expiredAt?: string;
    bankCode?: string;
  };
  onClose: () => void;
  onPaymentSuccess: (settledTrx: TransaksiPembayaran) => void;
}

// ─── Countdown Timer ─────────────────────────────────────────────────────────
function useCountdown(expiredAt?: string) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!expiredAt) return;
    const target = new Date(expiredAt).getTime();
    const tick = () => setRemaining(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiredAt]);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return { remaining, display: `${pad(h)}:${pad(m)}:${pad(s)}`, isExpired: remaining === 0 };
}

// ─── Main Modal ──────────────────────────────────────────────────────────────
export const VAPaymentModal: React.FC<VAPaymentModalProps> = ({
  transaction,
  onClose,
  onPaymentSuccess,
}) => {
  const bankInfo = VA_BANK_LIST.find(b => b.code === transaction.bankCode) || VA_BANK_LIST[0];
  const expiredAt = transaction.expiredAt || new Date(Date.now() + 60 * 60 * 1000).toISOString(); // default 1 jam
  const { remaining, display: countdown, isExpired } = useCountdown(expiredAt);

  const [copied, setCopied] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [expandedInstruction, setExpandedInstruction] = useState<'ATM' | 'MBANKING' | null>('MBANKING');
  const [pollCount, setPollCount] = useState(0);

  // Copy VA number to clipboard
  const handleCopyVA = () => {
    if (transaction.vaNumber) {
      navigator.clipboard.writeText(transaction.vaNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Manual check payment status
  const handleCheckStatus = useCallback(async () => {
    if (isChecking) return;
    try {
      setIsChecking(true);
      setCheckError('');
      const res = await api.checkPaymentStatus(transaction.noTransaksi);
      if (res.status === 'SUCCESS') {
        onPaymentSuccess(res.transaction || transaction);
      } else if (res.status === 'EXPIRED') {
        setCheckError('Waktu pembayaran telah habis. Buat transaksi baru.');
      } else {
        setCheckError('Pembayaran belum terkonfirmasi. Coba lagi setelah membayar.');
      }
    } catch (e: any) {
      setCheckError('Gagal memeriksa status. Periksa koneksi internet.');
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, transaction, onPaymentSuccess]);

  // Auto-poll every 15 seconds (max 20x = 5 menit)
  useEffect(() => {
    if (isExpired || pollCount >= 20) return;
    const timer = setTimeout(async () => {
      setPollCount(c => c + 1);
      try {
        const res = await api.checkPaymentStatus(transaction.noTransaksi);
        if (res.status === 'SUCCESS') {
          onPaymentSuccess(res.transaction || transaction);
        }
      } catch { /* silent fail on auto-poll */ }
    }, 15000);
    return () => clearTimeout(timer);
  }, [pollCount, isExpired, transaction, onPaymentSuccess]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[95vh] overflow-y-auto">

        {/* ── Header ── */}
        <div className={`p-5 text-white relative bg-gradient-to-r from-amber-600 to-amber-500`}>
          <button onClick={onClose} className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-lg transition cursor-pointer">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
              {bankInfo.logo}
            </div>
            <div>
              <h3 className="font-extrabold text-base">Virtual Account {bankInfo.bank}</h3>
              <p className="text-amber-100 text-[11px]">Selesaikan pembayaran sebelum waktu habis</p>
            </div>
          </div>
        </div>

        {/* ── Countdown Timer ── */}
        <div className={`px-5 py-3 flex items-center justify-between text-sm border-b ${
          isExpired ? 'bg-rose-50 border-rose-200' : remaining < 300 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="flex items-center gap-1.5">
            <Clock className={`w-4 h-4 ${isExpired ? 'text-rose-500' : remaining < 300 ? 'text-amber-500' : 'text-slate-500'}`} />
            <span className={`text-xs font-semibold ${isExpired ? 'text-rose-600' : 'text-slate-600'}`}>
              {isExpired ? 'Waktu Habis!' : 'Batas Waktu Bayar:'}
            </span>
          </div>
          <span className={`font-mono font-black text-base tabular-nums ${
            isExpired ? 'text-rose-600' : remaining < 300 ? 'text-amber-600' : 'text-emerald-600'
          }`}>
            {isExpired ? 'EXPIRED' : countdown}
          </span>
        </div>

        {/* ── VA Number ── */}
        <div className="px-5 pt-5 pb-3">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nomor Virtual Account {bankInfo.bank}</p>
          <div className={`flex items-center gap-3 bg-slate-50 border-2 ${bankInfo.borderColor} rounded-xl p-3.5`}>
            <span className={`font-mono text-xl font-black tracking-widest flex-1 ${bankInfo.textColor} select-all`}>
              {transaction.vaNumber || '—'}
            </span>
            <button
              onClick={handleCopyVA}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                copied ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Tersalin!' : 'Salin'}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center">
            Ketik nomor VA ini dengan tepat saat melakukan transfer
          </p>
        </div>

        {/* ── Payment Summary ── */}
        <div className="px-5 pb-4">
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3.5 space-y-1.5 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Nama Siswa:</span>
              <span className="font-bold text-slate-800">{transaction.namaSiswa}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>No. Transaksi:</span>
              <span className="font-mono text-slate-700">{transaction.noTransaksi}</span>
            </div>
            <div className="border-t border-sky-200 pt-1.5 flex justify-between items-center">
              <span className="font-bold text-slate-800">Total Dibayar:</span>
              <span className="font-mono text-base font-black text-sky-900">
                Rp {(transaction.totalBayar || 0).toLocaleString('id-ID')}
              </span>
            </div>
          </div>
        </div>

        {/* ── Instruction Accordion ── */}
        <div className="px-5 pb-3 space-y-2">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Cara Pembayaran</p>

          {/* Mobile Banking */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandedInstruction(expandedInstruction === 'MBANKING' ? null : 'MBANKING')}
              className="w-full flex items-center justify-between px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 transition cursor-pointer"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <Smartphone className="w-3.5 h-3.5 text-sky-600" />
                Mobile Banking / Internet Banking
              </div>
              {expandedInstruction === 'MBANKING' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedInstruction === 'MBANKING' && (
              <ol className="px-4 pb-3 pt-2 space-y-1.5 animate-in slide-in-from-top-1 duration-150">
                {bankInfo.mBankingSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[11px] text-slate-600">
                    <span className="w-4 h-4 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-[9px] shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* ATM */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandedInstruction(expandedInstruction === 'ATM' ? null : 'ATM')}
              className="w-full flex items-center justify-between px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 transition cursor-pointer"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <Monitor className="w-3.5 h-3.5 text-amber-600" />
                ATM {bankInfo.bank}
              </div>
              {expandedInstruction === 'ATM' ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedInstruction === 'ATM' && (
              <ol className="px-4 pb-3 pt-2 space-y-1.5 animate-in slide-in-from-top-1 duration-150">
                {bankInfo.atmSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[11px] text-slate-600">
                    <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[9px] shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* ── Status Check ── */}
        <div className="px-5 pb-5 space-y-2">
          {checkError && (
            <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {checkError}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleCheckStatus}
              disabled={isChecking || isExpired}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition ${
                !isChecking && !isExpired
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isChecking ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span>Memeriksa...</span></>
              ) : (
                <><RefreshCw className="w-4 h-4" /><span>Cek Status Pembayaran</span></>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 text-xs font-bold hover:bg-slate-50 transition cursor-pointer"
            >
              Tutup
            </button>
          </div>
          <p className="text-[10px] text-slate-400 text-center">
            Auto-cek setiap 15 detik · Status diperbarui otomatis setelah pembayaran berhasil
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Bank Selector Modal ──────────────────────────────────────────────────────
interface BankSelectorModalProps {
  totalPayment: number;
  siswaName: string;
  onSelectBank: (bankCode: string) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export const BankSelectorModal: React.FC<BankSelectorModalProps> = ({
  totalPayment,
  siswaName,
  onSelectBank,
  onClose,
  isLoading = false,
}) => {
  const [selectedCode, setSelectedCode] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-500 p-5 text-white relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-lg transition cursor-pointer">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base">Pilih Bank Virtual Account</h3>
              <p className="text-amber-100 text-[11px]">Sistem akan membuat nomor VA untuk bank yang dipilih</p>
            </div>
          </div>
        </div>

        {/* Payment summary */}
        <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-amber-900">{siswaName}</p>
            <p className="text-[10px] text-amber-600">Tagihan akan dibayar via VA</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500">Total Bayar</p>
            <p className="text-base font-extrabold font-mono text-amber-800">
              Rp {totalPayment.toLocaleString('id-ID')}
            </p>
          </div>
        </div>

        {/* Bank list */}
        <div className="p-5 space-y-2">
          {VA_BANK_LIST.map(bank => (
            <button
              key={bank.code}
              onClick={() => setSelectedCode(bank.code)}
              disabled={isLoading}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition cursor-pointer ${
                selectedCode === bank.code
                  ? `${bank.borderColor} bg-slate-50`
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="text-xl">{bank.logo}</span>
              <div className="flex-1">
                <p className={`text-sm font-bold ${selectedCode === bank.code ? bank.textColor : 'text-slate-800'}`}>
                  {bank.bank}
                </p>
                <p className="text-[10px] text-slate-500">{bank.label}</p>
              </div>
              {selectedCode === bank.code && (
                <CheckCircle2 className={`w-5 h-5 ${bank.textColor}`} />
              )}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition cursor-pointer"
          >
            Batal
          </button>
          <button
            onClick={() => selectedCode && onSelectBank(selectedCode)}
            disabled={!selectedCode || isLoading}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition ${
              selectedCode && !isLoading
                ? 'bg-amber-600 hover:bg-amber-700 text-white cursor-pointer shadow-sm'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /><span>Membuat VA...</span></>
            ) : (
              <><CreditCard className="w-4 h-4" /><span>Buat Virtual Account</span></>
            )}
          </button>
        </div>

        {/* Note */}
        <div className="bg-slate-50 border-t border-slate-100 px-5 py-2.5 text-[10px] text-slate-400 text-center">
          VA berlaku <strong>60 menit</strong> · Bayar sebelum batas waktu habis
        </div>
      </div>
    </div>
  );
};
